import { useEffect, useRef, useState } from "react";
import { Play, RotateCcw, Zap, X, Trophy, Save, UserPlus, Gamepad2 } from "lucide-react";
import { sounds } from "@/lib/sounds";
import { playRewardedAd, isAdsConfigured } from "@/lib/rewardedAds";

// AdsBitVex-backed rewarded ad overlay. When the SDK is configured we fire
// window.showadsbitvex() on mount and let its own full-screen player take
// over (this component renders nothing). On resolve → onDone (grant reward
// + resume game). On reject → onCancel (no ad served / user bailed early).
// When no App ID is configured we fall through to a 5-second demo countdown
// so local dev keeps working.
function AdOverlay({ onDone, onCancel }) {
  const [remaining, setRemaining] = useState(5);
  const startedRef = useRef(false);
  const timerRef = useRef(null);

  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    if (!isAdsConfigured()) {
      timerRef.current = setInterval(() => {
        setRemaining((r) => (r > 0 ? r - 1 : 0));
      }, 1000);
    }

    playRewardedAd()
      .then(() => {
        if (timerRef.current) clearInterval(timerRef.current);
        onDone();
      })
      .catch(() => {
        if (timerRef.current) clearInterval(timerRef.current);
        onCancel();
      });

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [onDone, onCancel]);

  // Production path — the SDK owns the entire screen; render nothing.
  if (isAdsConfigured()) return null;

  // Demo fallback UI (dev only).
  return (
    <div className="fixed inset-0 z-[80] bg-black/95 backdrop-blur-xl flex items-center justify-center p-6">
      <div
        data-testid="ad-overlay"
        className="max-w-md w-full border border-[#00F0FF]/50 bg-[#0B0C10] p-8 text-center relative"
      >
        <div className="text-[10px] uppercase tracking-[0.4em] text-[#94A3B8] mb-4">
          Sponsored — Rewarded Ad
        </div>
        <div className="h-48 border border-dashed border-[#00F0FF]/40 flex items-center justify-center mb-6">
          <div className="text-center">
            <Zap size={40} strokeWidth={2.5} className="text-[#EAEB18] mx-auto animate-pulse" />
            <div className="mt-3 font-heading font-bold text-white">
              Your Ad Unit Here
            </div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] mt-1">
              Demo Placeholder
            </div>
          </div>
        </div>
        <div className="font-mono-arcade text-3xl text-[#EAEB18] glow-yellow mb-2">
          {remaining}s
        </div>
        <div className="text-xs text-[#94A3B8] uppercase tracking-[0.2em] mb-6">
          Ad plays. Then you resume from the exact spot.
        </div>
        <button
          data-testid="ad-skip-btn"
          onClick={() => {
            sounds.play("click");
            onCancel();
          }}
          className="text-xs uppercase tracking-[0.2em] text-[#94A3B8] hover:text-[#FF007F] transition-colors"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}

// Guest sign-up strip rendered inside the Game Over card whenever the
// player is anonymous. Collects a unique username and posts the
// just-played score to the leaderboard via the parent's handler.
function GuestSignupBlock({ score, onRegister, onGoogleLogin }) {
  const [username, setUsername] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [saved, setSaved] = useState(false);

  const submit = async () => {
    const name = username.trim();
    setError(null);
    if (!/^[a-zA-Z0-9_]{3,20}$/.test(name)) {
      setError("3-20 chars · letters, numbers, underscores only");
      return;
    }
    setSaving(true);
    try {
      await onRegister(name);
      setSaved(true);
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail;
      if (status === 409) {
        setError("Username already taken — try another");
      } else {
        setError(detail || "Failed to save. Try again.");
      }
    } finally {
      setSaving(false);
    }
  };

  if (saved) {
    return (
      <div
        data-testid="guest-signup-saved"
        className="mb-5 border border-[#39FF14] bg-[#0B0C10] p-3 flex items-center gap-2"
      >
        <Trophy size={14} strokeWidth={2.5} className="text-[#39FF14]" />
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#39FF14] font-bold">
          Score saved to the leaderboard!
        </div>
      </div>
    );
  }

  return (
    <div
      data-testid="guest-signup-block"
      className="mb-5 border border-[#EAEB18] bg-[#EAEB18]/5 p-4"
    >
      <div className="flex items-center gap-2 mb-2">
        <UserPlus size={14} strokeWidth={2.5} className="text-[#EAEB18]" />
        <div className="font-heading font-bold text-[10px] uppercase tracking-[0.25em] text-[#EAEB18]">
          Enter username to save score to leaderboard
        </div>
      </div>
      <div className="flex flex-col sm:flex-row gap-2">
        <input
          data-testid="guest-username-input"
          type="text"
          value={username}
          onChange={(e) => {
            setUsername(e.target.value);
            setError(null);
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
          }}
          placeholder="your_username"
          autoComplete="off"
          spellCheck="false"
          maxLength={20}
          disabled={saving}
          className="flex-1 bg-[#050505] border border-[#EAEB18]/40 focus:border-[#EAEB18] outline-none text-white font-mono-arcade text-sm px-3 py-2.5"
        />
        <button
          data-testid="guest-save-btn"
          onClick={submit}
          disabled={saving || !username.trim()}
          className="inline-flex items-center justify-center gap-2 bg-[#EAEB18] text-black font-heading font-black text-xs uppercase tracking-widest px-4 py-2.5 hover:bg-white transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
        >
          <Save size={14} strokeWidth={2.5} />
          {saving ? "Saving..." : `Save · ${String(score).padStart(5, "0")}`}
        </button>
      </div>
      {error && (
        <div
          data-testid="guest-username-error"
          className="mt-2 text-[10px] uppercase tracking-[0.2em] text-[#FF007F] font-bold"
        >
          {error}
        </div>
      )}
      <div className="mt-3 pt-3 border-t border-[#EAEB18]/20">
        <button
          data-testid="guest-google-upgrade-btn"
          onClick={onGoogleLogin}
          className="w-full inline-flex items-center justify-center gap-2 text-[10px] uppercase tracking-[0.25em] text-[#00F0FF] hover:text-white border border-[#00F0FF]/50 hover:border-[#00F0FF] py-2 transition-colors"
        >
          <Gamepad2 size={14} strokeWidth={2.5} />
          Continue with Google
        </button>
      </div>
    </div>
  );
}

export default function GameOverModal({
  score,
  resumesLeft,
  isNewHighScore,
  isPro,
  isGuest = false,
  onGuestRegister,
  onGoogleLogin,
  onResume,
  onRetry,
  onClose,
}) {
  const [showAd, setShowAd] = useState(false);

  if (showAd) {
    return (
      <AdOverlay
        onDone={() => {
          setShowAd(false);
          onResume();
        }}
        onCancel={() => setShowAd(false)}
      />
    );
  }

  return (
    <div className="game-over-overlay">
      <div
        data-testid="game-over-modal"
        className="game-over-card border border-[#00F0FF]/50 border-t-[3px] border-t-[#FF007F] bg-[#0B0C10]/95 relative"
      >
        {/* Close (X) button — safely inside the card on mobile, protruding on desktop */}
        <button
          data-testid="game-over-close-btn"
          aria-label="Close game over popup"
          onClick={() => {
            sounds.play("click");
            onClose();
          }}
          className="game-over-close bg-[#FF007F] text-black border-2 border-black hover:bg-white hover:text-[#FF007F] hover:border-[#FF007F] transition-colors shadow-[3px_3px_0_#00F0FF] hover:shadow-[1px_1px_0_#00F0FF] hover:translate-x-[2px] hover:translate-y-[2px]"
        >
          <X size={22} strokeWidth={3} />
        </button>

        <div className="text-[10px] uppercase tracking-[0.4em] text-[#FF007F] mb-2 mt-2 sm:mt-0">
          Collision Detected
        </div>
        <div className="font-heading font-black text-4xl text-white mb-2">
          GAME OVER
        </div>

        {isNewHighScore && (
          <div
            data-testid="new-high-score-banner"
            className="mb-4 flex items-center gap-2 border border-[#EAEB18] bg-[#EAEB18]/10 px-3 py-2"
          >
            <Trophy size={14} strokeWidth={2.5} className="text-[#EAEB18]" />
            <span className="text-[10px] uppercase tracking-[0.3em] text-[#EAEB18] glow-yellow font-bold">
              New Personal Best!
            </span>
          </div>
        )}

        <div className="border border-[#00F0FF]/30 p-4 mb-6 mt-4">
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#94A3B8] mb-1">
            Final Score
          </div>
          <div
            data-testid="final-score"
            className="font-mono-arcade text-4xl font-bold text-[#EAEB18] glow-yellow"
          >
            {String(score).padStart(5, "0")}
          </div>
        </div>

        {isGuest && (
          <GuestSignupBlock
            score={score}
            onRegister={onGuestRegister}
            onGoogleLogin={onGoogleLogin}
          />
        )}

        <div className="flex flex-col gap-3">
          {!isGuest && (
            <button
              data-testid="resume-with-ad-btn"
              disabled={resumesLeft <= 0}
              onClick={() => {
                sounds.play("click");
                if (isPro) {
                  onResume();
                } else {
                  setShowAd(true);
                }
              }}
              className="btn-brutal rounded-none px-6 py-4 flex items-center justify-center gap-2"
            >
              <Play size={18} strokeWidth={2.5} />
              {isPro ? "Resume" : "Resume with Ad"}
              <span className="ml-1 text-[10px] tracking-normal opacity-70">
                ({resumesLeft} left)
              </span>
            </button>
          )}
          <button
            data-testid="retry-btn"
            onClick={() => {
              sounds.play("click");
              onRetry();
            }}
            className="btn-ghost-cyan rounded-none px-6 py-4 flex items-center justify-center gap-2"
          >
            <RotateCcw size={18} strokeWidth={2.5} />
            Retry
          </button>
        </div>

        {!isGuest && (
          <div className="mt-6 text-[10px] uppercase tracking-[0.2em] text-[#94A3B8] text-center">
            Score has been submitted to the leaderboard.
          </div>
        )}
      </div>
    </div>
  );
}
